import { Link, router } from "@inertiajs/react";
import { Building2, Eye } from "lucide-react";
import { CompanyLogo } from "@/components/CompanyLogo";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  bizminer_admin_organization_path,
  bizminer_admin_organizations_path,
} from "@/routes";
import type { PagyMetadata } from "@/types/Pagy";
import type { BizminerAdminOrganizationList } from "@/types/serializers";

interface Props {
  organizations: BizminerAdminOrganizationList[];
  pagy: PagyMetadata;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function OrganizationsTable({ organizations, pagy }: Props) {
  const handlePageChange = (page: number) => {
    router.get(
      bizminer_admin_organizations_path(),
      { page },
      { preserveState: true, preserveScroll: true },
    );
  };

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="text-lg">All Organizations</CardTitle>
        <CardDescription>
          Overview of all tenant organizations in the system.
        </CardDescription>
      </CardHeader>

      {organizations.length === 0 ? (
        <CardContent className="text-center py-12 text-muted-foreground">
          <Building2 className="mx-auto h-12 w-12 mb-4 opacity-50" />
          <p>No organizations found</p>
        </CardContent>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="px-6">Logo</TableHead>
                <TableHead className="px-6">Name</TableHead>
                <TableHead className="px-6">Slug</TableHead>
                <TableHead className="px-6">Users</TableHead>
                <TableHead className="px-6">Created</TableHead>
                <TableHead className="px-6 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell className="px-6 py-4">
                    <CompanyLogo
                      logoUrl={org.logo_thumb_url}
                      name={org.name}
                      className="h-8 w-8"
                    />
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <Link
                      href={bizminer_admin_organization_path(org.id)}
                      className="font-medium text-primary hover:text-primary/80"
                    >
                      {org.name}
                    </Link>
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <code className="text-muted-foreground font-mono bg-muted px-2 py-1 rounded">
                      {org.slug}
                    </code>
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <span className="text-foreground">{org.users_count}</span>
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <span className="text-muted-foreground">
                      {formatDate(org.created_at)}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <Button asChild size="sm">
                      <Link href={bizminer_admin_organization_path(org.id)}>
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {pagy.last > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <span className="text-sm text-muted-foreground">
                Showing {pagy.from} to {pagy.to} of {pagy.count} organizations
              </span>
              <Pagination className="mx-0 w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() =>
                        pagy.previous && handlePageChange(pagy.previous)
                      }
                      className={
                        !pagy.previous
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                  {Array.from({ length: pagy.last }, (_, i) => i + 1).map(
                    (page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => handlePageChange(page)}
                          isActive={page === pagy.page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => pagy.next && handlePageChange(pagy.next)}
                      className={
                        !pagy.next
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
